import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, type Control, type FieldErrors, type UseFormRegister, type UseFormSetValue } from 'react-hook-form'
import { AlertCircle, Calendar, Check, Info, Send, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { showApiError } from '@/lib/utils/notifications'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { useUserRole } from '@/hooks/useUserRole'
import {
  entityActionButtonClusterClass,
  deleteEntityActionButtonClass,
  primaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import type {
  ResidentApplicationData,
  ResidentDefault,
  ResidentDefaultFormValues,
  ResidentDocumentGroup,
} from '@/features/resident/types'
import type { RentalListResponse } from '@/features/resident/types/resident'
import { ResidentDateField } from '@/features/resident/components/ResidentDateField'
import { ReimbursementStatusBar } from './ReimbursementStatusBar'
import { ReimbursementFormStickyLayout } from './ReimbursementFormStickyLayout'
import { ReimbursementMilestoneSectionActionRow } from './ReimbursementMilestoneSectionActions'
import { ActionButtonSpinner } from './reimbursementActionButtonLoading'
import { ReimbursementSectionSubmitConfirm } from './ReimbursementSectionSubmitConfirm'
import { useIsBelowLg } from '../../hooks/useIsBelowLg'
import { getReimbursementMilestoneStatus } from '../../utils/reimbursementMilestoneStatus'
import {
  getReimbursementSectionActionMode,
  isPmcSectionFormDisabled,
  isSectionUnlockedForLiberty,
  resolveSectionWorkflowStatus,
  type ReimbursementSectionStatusValue,
} from '../../utils/reimbursementSectionStatus'
import { mergeReimbursementExtendedFields } from '../../utils/reimbursementExtendedFieldsHydration'
import {
  ReimbursementRequestDocumentRow,
  YesNoRadioGroup,
} from './ReimbursementRequestDocumentRow'
import { buildReimbursementStatusBarProps } from '../../utils/buildReimbursementStatusBarProps'
import {
  EVICTION_SHORT_PATH_DISABLED_DOC_TYPES,
  getEnabledDocTypes,
  getEvictionShortPathResetFields,
  getRejectedDocuments,
  getWorkflowProgress,
  isCoreSectionSubmitReady,
  isFinalSectionSubmitReady,
  isUniqueSectionSubmitReady,
} from '../../utils/reimbursementFormLogic'
import {
  syncApiDocumentsToLocalMap,
  buildReclassifyOptions,
  resolveAttachmentIdForDocType,
  getApiUploadFieldForDocType,
  resolveReimbursementDocumentStatusLabel,
  type ReimbursementImmediateUploadField,
} from '../../utils/reimbursementDocumentBridge'
import type {
  DocumentReviewIncompleteReason,
  ResidentStatusListResponse,
} from '@/features/resident/types/domain'
import { buildReimbursementReasonSelectOptions } from '@/features/resident/utils/residentListConstants'
import {
  createEmptyReimbursementDocumentsMap,
  type ReimbursementMilestoneId,
  DOCUMENT_TYPE_LABELS,
  EMPTY_REIMBURSEMENT_EXTENDED_FIELDS,
  type ReimbursementDocumentFile,
  type ReimbursementDocumentType,
  type ReimbursementDocumentsMap,
  type ReimbursementExtendedFields,
} from '../../types/reimbursementFormTypes'
import { formatDate } from '../../utils/formatters'
import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthToken } from '@/lib/api/apiClient'
import { EvictionLawsuitWarningModal } from '../modals/EvictionLawsuitWarningModal'
import { EvictionLawsuitHelpModal } from '../modals/EvictionLawsuitHelpModal'
import { AlternateCourtDocHelpModal } from '../modals/AlternateCourtDocHelpModal'
import { CancelReimbursementConfirmModal } from '../modals/CancelReimbursementConfirmModal'
import { getPropertyPaymentInfo } from '../../services/reimbursementsService'
import type { PropertyPaymentInfo } from '../../types'
import { reimbursementTypeDisplayLabel } from '@/features/resident/utils/residentHeaderStatus'

export interface ReimbursementRequestTabProps {
  control: Control<ResidentDefaultFormValues>
  register: UseFormRegister<ResidentDefaultFormValues>
  setValue?: UseFormSetValue<ResidentDefaultFormValues>
  values: ResidentDefaultFormValues
  errors: FieldErrors<ResidentDefaultFormValues>
  rentalList: RentalListResponse | null
  statusList?: ResidentStatusListResponse | null
  disabled: boolean
  roleName: string
  residentDefault?: ResidentDefault | null
  reimbursementDetail?: Record<string, unknown> | null
  applicationData?: ResidentApplicationData | null
  isLibertyRole?: boolean
  onOpenEorp?: () => void
  onImmediateDocumentUpload?: (
    fieldName: ReimbursementImmediateUploadField,
    files: File[],
  ) => void | Promise<void>
  onReviewDocument?: (
    docType: ReimbursementDocumentType,
    decision: 'approved' | 'incomplete',
    options?: { reason?: string; attachmentId?: string | number; email_send?: boolean },
  ) => Promise<void>
  onReclassifyDocument?: (
    fromDocType: ReimbursementDocumentType,
    toDocType: ReimbursementDocumentType,
    options?: { attachmentId?: string | number },
  ) => Promise<void>
  documentReviewIncompleteReasons?: DocumentReviewIncompleteReason[]
  documentUploadingField?: string | null
  residentDocuments?: ResidentDocumentGroup
  onDownloadDocument?: (
    fileTypeKey: string,
    row: { attachment_id?: string | number; filepath?: string; filename?: string },
  ) => void
  onRemoveDocument?: (attachmentId: string | number) => void
  removingDocumentAttachmentId?: string | number | null
  canUploadDocuments?: boolean
  canRemoveDocuments?: boolean
  onRentalStatusDateUserChange?: (value: string) => void
  onFirstMonthChange?: () => void
  onCancelReimbursement?: () => void
  saveDisabled?: boolean
  sectionSaveLoading?: boolean
  onSectionSave?: () => void
  onRegisterExtendedFields?: (fields: ReimbursementExtendedFields) => void
  onSubmitReimbursementSection?: (milestoneId: ReimbursementMilestoneId) => Promise<void>
  onUnlockReimbursementSection?: (milestoneId: ReimbursementMilestoneId) => Promise<void>
  onSubmitForLibertyReview?: () => Promise<void>
}

const fieldLabelClass = 'flex flex-col gap-1 text-sm font-medium text-slate-700'
const fieldClass = 'rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900'

function reimbursementValueFieldProps(isConcluded: boolean, sectionLocked: boolean) {
  return isConcluded
    ? { readOnly: true as const, disabled: false as const }
    : { readOnly: false as const, disabled: sectionLocked }
}

function ReasonForIncidentSelect({
  register,
  options,
  disabled: selectDisabled,
  readOnly,
}: {
  register: UseFormRegister<ResidentDefaultFormValues>
  options: Array<{ value: string; label: string }>
  disabled: boolean
  readOnly?: boolean
}) {
  const locked = Boolean(selectDisabled || readOnly)
  return (
    <label className="flex flex-col gap-1 text-sm font-medium text-[#173f7a]">
      Reason for Incident
      <select
        className={cn(
          fieldClass,
          'bg-white text-[#173f7a]',
          readOnly &&
            'disabled:cursor-default disabled:bg-white disabled:text-[#173f7a] disabled:opacity-100',
        )}
        {...register('reason_for_default')}
        disabled={locked}
      >
        <option value="">Select a reason...</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  )
}

export function ReimbursementRequestTab({
  control,
  register,
  setValue,
  values,
  errors,
  rentalList,
  statusList = null,
  disabled,
  residentDefault,
  reimbursementDetail,
  applicationData,
  isLibertyRole,
  onOpenEorp,
  onImmediateDocumentUpload,
  onReviewDocument,
  onReclassifyDocument,
  documentReviewIncompleteReasons = [],
  documentUploadingField,
  residentDocuments,
  onDownloadDocument,
  onRemoveDocument,
  removingDocumentAttachmentId = null,
  canUploadDocuments,
  canRemoveDocuments,
  onFirstMonthChange,
  onCancelReimbursement,
  saveDisabled,
  sectionSaveLoading = false,
  onSectionSave,
  onRegisterExtendedFields,
  onSubmitReimbursementSection,
  onUnlockReimbursementSection,
  onSubmitForLibertyReview,
}: ReimbursementRequestTabProps) {
  const incidentReasonOptions = useMemo(
    () => buildReimbursementReasonSelectOptions(statusList, rentalList),
    [statusList, rentalList],
  )
  const [extended, setExtended] = useState<ReimbursementExtendedFields>(EMPTY_REIMBURSEMENT_EXTENDED_FIELDS)
  /** Latest extended fields (including in-flight setState) for merge after silent GET reloads. */
  const extendedMergeRef = useRef(extended)
  extendedMergeRef.current = extended
  const [localDocuments, setLocalDocuments] = useState<ReimbursementDocumentsMap>(
    createEmptyReimbursementDocumentsMap(),
  )
  const prefilledSkipAffidavitUrl = useMemo(() => {
    const applicationId = applicationData?.application_id
    if (!applicationId) return null
    const token = getAuthToken()
    const base = `${getBrowserApiBaseUrl()}/api/skip_claim_affidavit/${applicationId}`
    return token ? `${base}?access_token=${encodeURIComponent(token)}` : base
  }, [applicationData?.application_id])
  const [showEvictionWarning, setShowEvictionWarning] = useState(false)
  const [pendingEvictionLawsuitFile, setPendingEvictionLawsuitFile] = useState<File | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelConfirmValue, setCancelConfirmValue] = useState('')
  const [propertyPaymentInfo, setPropertyPaymentInfo] = useState<PropertyPaymentInfo | null>(null)
  const [sectionActionLoading, setSectionActionLoading] = useState<{
    milestoneId: ReimbursementMilestoneId
    kind: 'submit' | 'unlock'
  } | null>(null)
  const [pendingSubmittedMilestones, setPendingSubmittedMilestones] = useState<
    Set<ReimbursementMilestoneId>
  >(() => new Set())
  const [pendingMobileSubmitMilestoneId, setPendingMobileSubmitMilestoneId] = useState<
    number | null
  >(null)
  const [documentReviewState, setDocumentReviewState] = useState<{
    docType: ReimbursementDocumentType
    action: 'approve' | 'incomplete' | 'reclassify'
    attachmentId?: string | number
  } | null>(null)
  const [submitForReviewLoading, setSubmitForReviewLoading] = useState(false)
  const isBelowLg = useIsBelowLg()
  const { user } = useUserRole()
  const currentUserDisplayName = useMemo(() => {
    const first = String(user?.first_name ?? '').trim()
    const last = String(user?.last_name ?? '').trim()
    const name = `${first} ${last}`.trim()
    return name || 'Current User'
  }, [user])

  const reimbursementType = reimbursementTypeDisplayLabel(values.reimbursement_type)
  const isPmcRole = !isLibertyRole

  const residentDefaultRecord = residentDefault as Record<string, unknown> | null | undefined
  const reimbursementDetailRecord = useMemo(() => {
    if (reimbursementDetail && typeof reimbursementDetail === 'object') {
      return reimbursementDetail
    }
    return null
  }, [reimbursementDetail])

  const sectionWorkflowStatus = useMemo(() => {
    const resolve = (milestoneId: ReimbursementMilestoneId): ReimbursementSectionStatusValue => {
      const fromApi = resolveSectionWorkflowStatus(
        residentDefaultRecord,
        reimbursementDetailRecord,
        milestoneId,
      )
      if (fromApi === 'SUBMITTED' || fromApi === 'APPROVED' || fromApi === 'UNLOCKED') {
        return fromApi
      }
      if (pendingSubmittedMilestones.has(milestoneId)) {
        return 'SUBMITTED'
      }
      return fromApi
    }
    return {
      1: resolve(1),
      2: resolve(2),
      3: resolve(3),
    }
  }, [pendingSubmittedMilestones, reimbursementDetailRecord, residentDefaultRecord])

  useEffect(() => {
    setPendingSubmittedMilestones((prev) => {
      if (prev.size === 0) return prev
      const next = new Set(prev)
      for (const milestoneId of [1, 2, 3] as const) {
        const fromApi = resolveSectionWorkflowStatus(
          residentDefaultRecord,
          reimbursementDetailRecord,
          milestoneId,
        )
        if (fromApi === 'SUBMITTED' || fromApi === 'APPROVED' || fromApi === 'UNLOCKED' || fromApi === 'DRAFT') {
          next.delete(milestoneId)
        }
      }
      return next.size === prev.size ? prev : next
    })
  }, [reimbursementDetailRecord, residentDefaultRecord])

  const formSaveInProgress = Boolean(sectionSaveLoading)
  const documentUploadInProgress = Boolean(
    documentUploadingField && formSaveInProgress,
  )
  const sectionActionsFrozen =
    formSaveInProgress || documentUploadInProgress || Boolean(sectionActionLoading)
  const sectionDraftSaveLoading = formSaveInProgress && !documentUploadInProgress

  const isConcluded = useMemo(() => {
    const norm = (v: unknown) => String(v ?? '').trim().toUpperCase()
    const status = norm(
      reimbursementDetailRecord?.reimbursement_rental_status ??
        residentDefaultRecord?.reimbursement_rental_status ??
        values.reimbursement_rental_status,
    )
    return status === 'CONCLUDED'
  }, [
    reimbursementDetailRecord,
    residentDefaultRecord,
    values.reimbursement_rental_status,
  ])
  const fieldsDisabled = disabled || isConcluded

  const isPmcSectionLocked = useCallback(
    (milestoneId: ReimbursementMilestoneId) =>
      isPmcSectionFormDisabled(sectionWorkflowStatus[milestoneId], isPmcRole),
    [isPmcRole, sectionWorkflowStatus],
  )

  const pmcSectionDisabled = useCallback(
    (milestoneId: ReimbursementMilestoneId) => fieldsDisabled || isPmcSectionLocked(milestoneId),
    [fieldsDisabled, isPmcSectionLocked],
  )

  const handleSectionSubmit = useCallback(
    async (milestoneId: number) => {
      if (milestoneId !== 1 && milestoneId !== 2 && milestoneId !== 3) return
      const id = milestoneId as ReimbursementMilestoneId
      setSectionActionLoading({ milestoneId: id, kind: 'submit' })
      try {
        if (onSubmitReimbursementSection) {
          await onSubmitReimbursementSection(id)
        } else {
          onSectionSave?.()
        }
        setPendingSubmittedMilestones((prev) => new Set(prev).add(id))
      } catch (err) {
        showApiError(err)
      } finally {
        setSectionActionLoading(null)
      }
    },
    [onSectionSave, onSubmitReimbursementSection],
  )

  const handleUnlockSection = useCallback(
    async (milestoneId: number) => {
      if (milestoneId !== 1 && milestoneId !== 2 && milestoneId !== 3) return
      if (!onUnlockReimbursementSection) return
      const id = milestoneId as ReimbursementMilestoneId
      setSectionActionLoading({ milestoneId: id, kind: 'unlock' })
      try {
        await onUnlockReimbursementSection(id)
      } catch (err) {
        showApiError(err)
      } finally {
        setSectionActionLoading(null)
      }
    },
    [onUnlockReimbursementSection],
  )

  const sectionUnlockedForLiberty = useMemo(
    () => ({
      1: isSectionUnlockedForLiberty(sectionWorkflowStatus[1]),
      2: isSectionUnlockedForLiberty(sectionWorkflowStatus[2]),
      3: isSectionUnlockedForLiberty(sectionWorkflowStatus[3]),
    }),
    [sectionWorkflowStatus],
  )

  useEffect(() => {
    if (residentDefaultRecord) {
      setExtended((prev) =>
        mergeReimbursementExtendedFields(residentDefaultRecord, {
          ...prev,
          ...extendedMergeRef.current,
        }),
      )
    }
  }, [residentDefaultRecord])

  useEffect(() => {
    onRegisterExtendedFields?.(extended)
  }, [extended, onRegisterExtendedFields])

  useEffect(() => {
    setLocalDocuments((prev) => syncApiDocumentsToLocalMap(values, residentDocuments, prev))
  }, [values, residentDocuments])

  const logicInput = useMemo(
    () => ({ values, extended, documents: localDocuments }),
    [values, extended, localDocuments],
  )

  const enabledDocTypes = useMemo(() => getEnabledDocTypes(logicInput), [logicInput])
  const workflowProgress = useMemo(() => getWorkflowProgress(logicInput), [logicInput])
  const rejectedDocs = useMemo(() => getRejectedDocuments(logicInput), [logicInput])

  const sectionSubmitReady = useMemo(
    () => ({
      1: isCoreSectionSubmitReady(logicInput, residentDocuments),
      2: isUniqueSectionSubmitReady(logicInput, residentDocuments),
      3: isFinalSectionSubmitReady(logicInput, residentDocuments),
    }),
    [logicInput, residentDocuments],
  )

  const statusBarProps = buildReimbursementStatusBarProps({
    values,
    residentDocuments,
    extended,
    localDocuments,
  })

  const updateExtended = useCallback(
    (patch: Partial<ReimbursementExtendedFields>) => {
      setExtended((prev) => {
        const next = { ...prev, ...patch }
        extendedMergeRef.current = next
        onRegisterExtendedFields?.(next)
        return next
      })
    },
    [onRegisterExtendedFields],
  )

  const rawPropertyId =
    applicationData?.fk_property_id ??
    applicationData?.property?.property_id ??
    residentDefault?.fk_property_id
  const propertyId =
    typeof rawPropertyId === 'string' || typeof rawPropertyId === 'number'
      ? rawPropertyId
      : null

  useEffect(() => {
    if (propertyId == null || propertyId === '') return
    let cancelled = false

    void getPropertyPaymentInfo(propertyId)
      .then((info) => {
        if (cancelled) return
        const verifyValue = String(info.verify ?? '').trim().toUpperCase()
        const verified = ['1', 'YES', 'TRUE', 'VERIFIED'].includes(verifyValue)
        setPropertyPaymentInfo(info)
        setExtended((prev) =>
          prev.payment_info_confirmed === verified
            ? prev
            : { ...prev, payment_info_confirmed: verified },
        )
      })
      .catch(() => {
        if (cancelled) return
        setPropertyPaymentInfo(null)
        setExtended((prev) =>
          prev.payment_info_confirmed
            ? { ...prev, payment_info_confirmed: false }
            : prev,
        )
      })

    return () => {
      cancelled = true
    }
  }, [propertyId])

  const updateLocalDocument = useCallback(
    (docType: ReimbursementDocumentType, file: ReimbursementDocumentFile | null) => {
      setLocalDocuments((prev) => ({ ...prev, [docType]: file }))
    },
    [],
  )

  const handleLocalFileSelect = useCallback(
    (docType: ReimbursementDocumentType, file: File) => {
      updateLocalDocument(docType, {
        file,
        fileName: file.name,
        fileSize: file.size,
        status: 'uploaded',
        uploadedAt: new Date(),
        uploadedBy: currentUserDisplayName,
      })
    },
    [currentUserDisplayName, updateLocalDocument],
  )

  const handleDocumentReview = useCallback(
    async (
      docType: ReimbursementDocumentType,
      status: 'approved' | 'incomplete',
      reason?: string,
      explicitAttachmentId?: string | number,
    ) => {
      const attachmentId =
        explicitAttachmentId ??
        resolveAttachmentIdForDocType(docType, localDocuments[docType], residentDocuments)

      if (onReviewDocument) {
        setDocumentReviewState({
          docType,
          action: status === 'approved' ? 'approve' : 'incomplete',
          attachmentId,
        })
        try {
          await onReviewDocument(docType, status, {
            reason,
            attachmentId,
            email_send: false,
          })
        } finally {
          setDocumentReviewState(null)
        }
        return
      }

      setLocalDocuments((prev) => {
        const current = prev[docType]
        if (!current) return prev
        return {
          ...prev,
          [docType]: {
            ...current,
            status,
            rejectionReason: status === 'incomplete' ? reason : undefined,
            reviewedAt: new Date(),
            reviewedBy: currentUserDisplayName,
          },
        }
      })
    },
    [currentUserDisplayName, localDocuments, onReviewDocument, residentDocuments],
  )

  const handleLocalReclassify = useCallback(
    async (fromDocType: ReimbursementDocumentType, toDocType: ReimbursementDocumentType) => {
      setDocumentReviewState({ docType: fromDocType, action: 'reclassify' })
      try {
        if (onReclassifyDocument) {
          const attachmentId = resolveAttachmentIdForDocType(
            fromDocType,
            localDocuments[fromDocType],
            residentDocuments,
          )
          await onReclassifyDocument(fromDocType, toDocType, { attachmentId })
          return
        }
        setLocalDocuments((prev) => {
          const source = prev[fromDocType]
          if (!source) return prev
          return {
            ...prev,
            [fromDocType]: null,
            [toDocType]: {
              ...source,
              reclassifiedFrom: DOCUMENT_TYPE_LABELS[fromDocType],
              reclassifiedAt: new Date(),
              reclassifiedBy: currentUserDisplayName,
            },
          }
        })
      } finally {
        setDocumentReviewState(null)
      }
    },
    [currentUserDisplayName, localDocuments, onReclassifyDocument, residentDocuments],
  )

  const handleSkippedDuringEvictionChange = (val: boolean) => {
    updateExtended({
      skipped_during_eviction: val,
      ...(val ? getEvictionShortPathResetFields() : {}),
    })
    if (val) {
      setLocalDocuments((prev) => {
        const next = { ...prev }
        for (const t of EVICTION_SHORT_PATH_DISABLED_DOC_TYPES) {
          next[t] = null
        }
        return next
      })
    }
  }

  const renderDocumentRow = (
    docType: ReimbursementDocumentType,
    options?: {
      helperText?: string
      appearance?: 'card' | 'compact'
      className?: string
      disabled?: boolean
      pmcSectionMilestone?: ReimbursementMilestoneId
      forceRender?: boolean
      headerExtra?: React.ReactNode
      tone?: 'default' | 'eviction' | 'skipped'
      labelOverride?: string
    },
  ) => {
    const pmcLocked =
      options?.pmcSectionMilestone != null && isPmcSectionLocked(options.pmcSectionMilestone)
    if (!options?.forceRender && !enabledDocTypes.includes(docType)) return null
    const uploadField = getApiUploadFieldForDocType(docType)?.fieldName
    const isUploading = Boolean(
      formSaveInProgress &&
        documentUploadingField &&
        uploadField === documentUploadingField,
    )
    const isReviewing = documentReviewState?.docType === docType
    const reviewPendingAction =
      documentReviewState?.docType === docType ? documentReviewState.action : null

    return (
      <ReimbursementRequestDocumentRow
        key={docType}
        docType={docType}
        document={localDocuments[docType]}
        disabled={fieldsDisabled || options?.disabled || pmcLocked}
        uploading={isUploading && !isReviewing}
        canUpload={canUploadDocuments}
        canRemove={canRemoveDocuments}
        isLibertyRole={isLibertyRole}
        residentDocuments={residentDocuments}
        statusLabel={resolveReimbursementDocumentStatusLabel(values, docType, residentDocuments)}
        helperText={options?.helperText}
        headerExtra={options?.headerExtra}
        appearance={options?.appearance}
        tone={options?.tone}
        labelOverride={options?.labelOverride}
        className={options?.className}
        onLocalFileSelect={(file) => handleLocalFileSelect(docType, file)}
        onImmediateUpload={onImmediateDocumentUpload}
        onLocalStatusChange={(status, reason, attachmentId) =>
          void handleDocumentReview(docType, status, reason, attachmentId)
        }
        documentReviewIncompleteReasons={documentReviewIncompleteReasons}
        documentReviewBusy={isReviewing}
        documentReviewPendingAction={reviewPendingAction}
        documentReviewAttachmentId={
          isReviewing ? documentReviewState?.attachmentId ?? null : null
        }
        onLocalRemove={() => updateLocalDocument(docType, null)}
        onLocalReclassify={(toDocType) =>
          handleLocalReclassify(docType, toDocType as ReimbursementDocumentType)
        }
        reclassifyOptions={buildReclassifyOptions({
          fromDocType: docType,
          enabledDocTypes,
          localDocuments,
          residentDocuments,
        })}
        onRemoveUploaded={onRemoveDocument}
        removingUploadedAttachmentId={removingDocumentAttachmentId}
        onDownloadUploaded={
          onDownloadDocument
            ? (row) => onDownloadDocument(getDownloadKey(docType), row)
            : undefined
        }
        showSkipAffidavitButton={docType === 'skip_affidavit' && !localDocuments.skip_affidavit}
        prefilledSkipAffidavitUrl={prefilledSkipAffidavitUrl}
        canOpenPrefilledSkipAffidavit={Boolean(canUploadDocuments && prefilledSkipAffidavitUrl)}
        onBeforeEvictionUpload={(file) => {
          setPendingEvictionLawsuitFile(file)
          setShowEvictionWarning(true)
          return false
        }}
      />
    )
  }

  const showRejectionBanner = workflowProgress === 'action_needed' && rejectedDocs.length > 0
  const canSubmitForReview = useMemo(() => {
    if (!isLibertyRole) return false
    const norm = (v: unknown) => String(v ?? '').trim().toUpperCase()
    const status = norm(
      reimbursementDetailRecord?.reimbursement_rental_status ??
        residentDefaultRecord?.reimbursement_rental_status ??
        values.reimbursement_rental_status,
    )
    const progress = norm(
      reimbursementDetailRecord?.reimbursement_rental_progress ??
        residentDefaultRecord?.reimbursement_rental_progress ??
        values.reimbursement_rental_progress,
    )
    return status === 'SUBMITTED' && progress === 'READY_FOR_REVIEW'
  }, [
    isLibertyRole,
    reimbursementDetailRecord,
    residentDefaultRecord,
    values.reimbursement_rental_progress,
    values.reimbursement_rental_status,
  ])
  const coreSectionDisabled = pmcSectionDisabled(1)
  const uniqueSectionDisabled = pmcSectionDisabled(2)
  const finalSectionDisabled = pmcSectionDisabled(3)
  const evictionFullPathDisabled =
    fieldsDisabled || uniqueSectionDisabled || extended.skipped_during_eviction !== false

  const paymentVerifyValue = String(propertyPaymentInfo?.verify ?? '').trim().toUpperCase()
  const paymentInfoVerified = ['1', 'YES', 'TRUE', 'VERIFIED'].includes(paymentVerifyValue)
  const paymentMethodRaw = String(
    propertyPaymentInfo?.payment_method ??
      applicationData?.payment_method ??
      residentDefault?.payment_method ??
      '',
  ).trim()
  const paymentMethodLabel =
    paymentMethodRaw.toUpperCase() === 'PAPER_CHECK'
      ? 'Paper Check'
      : paymentMethodRaw.toUpperCase() === 'ACH'
        ? 'ACH'
        : paymentMethodRaw.replace(/_/g, ' ') || 'Payment method unavailable'
  const paymentPayeeLabel =
    String(
      propertyPaymentInfo?.payee_user_name ??
        propertyPaymentInfo?.company_name ??
        propertyPaymentInfo?.property_name ??
        applicationData?.property?.company_name ??
        '',
    ).trim() || 'Not available'
  const paymentCityStateZip = [
    String(propertyPaymentInfo?.payment_city ?? '').trim(),
    [
      String(propertyPaymentInfo?.payment_state ?? '').trim(),
      String(propertyPaymentInfo?.payment_zipcode ?? '').trim(),
    ]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join(', ')
  const paymentAddressLabel =
    [
      String(propertyPaymentInfo?.payment_address ?? applicationData?.mailing_address ?? '').trim(),
      paymentCityStateZip,
    ]
      .filter(Boolean)
      .join(', ') || 'Not available'

  const renderFormSectionActions = (milestoneId: ReimbursementMilestoneId) => {
    if (!isBelowLg) return null

    const status = getReimbursementMilestoneStatus(statusBarProps, milestoneId)
    const workflowStatus = sectionWorkflowStatus[milestoneId]
    const milestoneActionMode = getReimbursementSectionActionMode(
      Boolean(isLibertyRole),
      workflowStatus,
    )

    return (
      <div
        className="mt-4 border-t border-slate-200 pt-4 lg:hidden"
        data-testid={`reimbursement-form-section-actions-${milestoneId}`}
      >
        <ReimbursementMilestoneSectionActionRow
          milestoneId={milestoneId}
          status={status}
          sectionActionMode={milestoneActionMode}
          sectionUnlocked={sectionUnlockedForLiberty}
          sectionWorkflowStatus={sectionWorkflowStatus[milestoneId]}
          sectionSubmitReady={sectionSubmitReady[milestoneId]}
          saveDisabled={saveDisabled || sectionActionsFrozen || isConcluded}
          sectionActionsLocked={isConcluded}
          sectionSaveLoading={sectionDraftSaveLoading}
          sectionActionLoading={sectionActionLoading}
          onSaveDraft={onSectionSave}
          onSubmitClick={() => setPendingMobileSubmitMilestoneId(milestoneId)}
          onUnlock={() => handleUnlockSection(milestoneId)}
        />
      </div>
    )
  }

  return (
    <div data-testid="reimbursement-request-tab">
      <ReimbursementFormStickyLayout
        statusBar={
          <ReimbursementStatusBar
            {...statusBarProps}
            showSectionActions
            isLibertyRole={isLibertyRole}
          sectionUnlocked={sectionUnlockedForLiberty}
          sectionWorkflowStatus={sectionWorkflowStatus}
          sectionSubmitReady={sectionSubmitReady}
          saveDisabled={saveDisabled || sectionActionsFrozen || isConcluded}
          sectionActionsLocked={isConcluded}
          sectionSaveLoading={sectionDraftSaveLoading}
          sectionActionLoading={sectionActionLoading}
          onSaveDraft={onSectionSave}
          onSectionSubmit={handleSectionSubmit}
          onUnlockSection={handleUnlockSection}
        />
        }
      >
        <div className="space-y-6">
          {showRejectionBanner ? (
            <div className="overflow-hidden rounded-xl border border-red-300">
              <div className="bg-red-600 px-4 py-3 text-sm font-semibold text-white">
                Documents Require Your Attention
              </div>
              <div className="space-y-3 bg-red-50 px-4 py-4">
                <p className="text-sm italic text-red-800">Action required before this request can proceed</p>
                <p className="text-sm text-red-900">
                  One or more files you uploaded marked incomplete after review by our Reimbursement Team:
                </p>
                <ul className="list-disc space-y-2 pl-5 text-sm text-red-900">
                  {rejectedDocs.map(({ docType, reason }) => (
                    <li key={docType}>
                      <strong>{DOCUMENT_TYPE_LABELS[docType as ReimbursementDocumentType]}</strong>
                      {reason ? ` — ${reason}` : ''}
                      {reason ? (
                        <p className="mt-0.5 text-xs text-red-700">
                          {lookupIncompleteReasonDescription(reason, documentReviewIncompleteReasons)}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-red-900">
                  Please upload the appropriate document or contact the Reimbursement Team with any questions.
                </p>
              </div>
            </div>
          ) : null}

          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
            <div className="mb-3 flex items-center gap-2">
              <AlertCircle className="text-orange-600" size={18} />
              <h3 className="text-sm font-semibold text-orange-900">Process Reminders</h3>
            </div>
            {onOpenEorp ? (
              <button
                type="button"
                onClick={onOpenEorp}
                className="mb-3 text-sm font-semibold text-orange-800 underline-offset-2 hover:underline"
                data-testid="reimbursement-request-eorp-link"
              >
                Explanation of the Reimbursement Process
              </button>
            ) : null}
            <ul className="list-disc space-y-1 pl-5 text-sm text-orange-900">
              <li>LR only covers nonpayment of rent. All other lease violations are ineligible for reimbursement.</li>
              <li>Unit must be returned to market within 10 days of move out date.</li>
              <li>
                Requests cannot be finalized until the reimbursement period ends (i.e. new resident move-in, six month
                max period ends, or Liberty Rent coverage ends).
              </li>
              <li>
                Reimbursement payments will be issued by means of the verified method (ACH or paper check) and the
                verified address on file.
              </li>
            </ul>
          </div>
          <section className="rounded-xl border border-slate-300 bg-white p-4 sm:p-6">
            <h3 className="text-base font-bold text-slate-900">Core Information</h3>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={fieldLabelClass}>
                Reimbursement Req Date
                <input
                  type="text"
                  readOnly
                  disabled
                  value={formatDate(values.rental_status_date) || ''}
                  className={cn(fieldClass, 'h-10 cursor-not-allowed bg-slate-100 text-slate-700')}
                  data-testid="reimbursement-request-req-date"
                />
              </label>

              <label className={fieldLabelClass}>
                First Month Rent Unpaid
                <div className="relative">
                  <input
                    type="month"
                    value={toMonthInputValue(
                      values.first_month_not_paid_month,
                      values.first_month_not_paid_year,
                    )}
                    readOnly={isConcluded && coreSectionDisabled}
                    disabled={!isConcluded && coreSectionDisabled}
                    onChange={(e) => {
                      const next = e.target.value
                      if (!next) {
                        setValue?.('first_month_not_paid_month', '', { shouldDirty: true })
                        setValue?.('first_month_not_paid_year', '', { shouldDirty: true })
                      } else {
                        const [year, month] = next.split('-')
                        setValue?.('first_month_not_paid_month', String(Number(month)), {
                          shouldDirty: true,
                        })
                        setValue?.('first_month_not_paid_year', year ?? '', { shouldDirty: true })
                      }
                      onFirstMonthChange?.()
                    }}
                    className={cn(
                      fieldClass,
                      'h-10 w-full pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0',
                      isConcluded && coreSectionDisabled && 'cursor-default pointer-events-none',
                    )}
                    data-testid="reimbursement-request-first-month-unpaid"
                  />
                  <Calendar
                    className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
              </label>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <YesNoRadioGroup
                name="lease_modifications_at_move_in"
                label="Was there a unit transfer after move-in?"
                value={extended.lease_modifications_at_move_in}
                onChange={(val) => updateExtended({ lease_modifications_at_move_in: val })}
                {...reimbursementValueFieldProps(isConcluded, coreSectionDisabled)}
              />
              {extended.lease_modifications_at_move_in !== null ? (
                <div className="mt-4 space-y-4 border-l border-slate-200 pl-7">
                  {extended.lease_modifications_at_move_in === true
                    ? renderDocumentRow('original_lease', {
                        appearance: 'compact',
                        helperText: 'Pre-modification lease document',
                        pmcSectionMilestone: 1,
                      })
                    : null}
                  {renderDocumentRow('signed_lease', {
                    appearance: 'compact',
                    helperText: 'Final lease document',
                    pmcSectionMilestone: 1,
                  })}
                </div>
              ) : null}
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              {renderDocumentRow('current_ledger', {
                appearance: 'compact',
                helperText: 'As of the time of this Reimbursement Request',
                pmcSectionMilestone: 1,
              })}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 md:grid-cols-2 md:items-center">
              <YesNoRadioGroup
                name="security_deposit_collected"
                label="Security Deposit Collected?"
                value={extended.security_deposit_collected ?? false}
                onChange={(val) => {
                  updateExtended({ security_deposit_collected: val })
                  if (val !== true) {
                    setValue?.('unused_security_deposit', '', { shouldDirty: true })
                  }
                }}
                {...reimbursementValueFieldProps(isConcluded, coreSectionDisabled)}
              />
              <label
                className={cn(
                  fieldLabelClass,
                  extended.security_deposit_collected !== true && 'text-slate-400',
                )}
              >
                Security Deposit Amount
                <Controller
                  control={control}
                  name="unused_security_deposit"
                  render={({ field }) => (
                    <CurrencyInput
                      lockPrefix
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      {...(isConcluded && coreSectionDisabled
                        ? { readOnly: true, disabled: false }
                        : {
                            readOnly: false,
                            disabled:
                              coreSectionDisabled || extended.security_deposit_collected !== true,
                          })}
                      data-testid="reimbursement-request-security-deposit-amount"
                      className={cn(
                        fieldClass,
                        'h-10',
                        extended.security_deposit_collected !== true &&
                          'bg-slate-100 text-slate-400 placeholder:text-slate-400',
                      )}
                    />
                  )}
                />
              </label>
            </div>
            {renderFormSectionActions(1)}
          </section>

          {reimbursementType === 'Skipped' ? (
            <section
              className="space-y-4 rounded-xl border border-[#5aa9ec] bg-[#eef6ff] p-4 text-[#173f7a] sm:p-6"
              data-testid="skipped-unique-requirements"
            >
              <h3 className="text-sm font-bold text-[#173f7a]">Skipped — Unique Requirements</h3>

              <div className="text-sm text-[#173f7a]">
                <p className="font-semibold text-[#173f7a]">Legal Skip Affidavit</p>
                <ul className="list-disc space-y-0.5 pl-6">
                  <li>Filed within same first calendar month rent was not paid</li>
                  <li>Via LR portal; authorized contact must complete, sign &amp; e-file</li>
                  <li>Resident voluntarily vacates — with or without notice</li>
                </ul>
              </div>

              <ReasonForIncidentSelect
                register={register}
                options={incidentReasonOptions}
                {...reimbursementValueFieldProps(isConcluded, uniqueSectionDisabled)}
              />

              <YesNoRadioGroup
                name="cares_act_applicable_skipped"
                label="Is a 30 day notice required at this property?"
                value={extended.cares_act_applicable}
                onChange={(val) => updateExtended({ cares_act_applicable: val })}
                {...reimbursementValueFieldProps(isConcluded, uniqueSectionDisabled)}
                tone="skipped"
              />

              {extended.cares_act_applicable === true ? (
                <div data-testid="skipped-notice-upload">
                  {renderDocumentRow('cares_act_notice', {
                    appearance: 'compact',
                    tone: 'skipped',
                    pmcSectionMilestone: 2,
                    forceRender: true,
                  })}
                </div>
              ) : null}

              <div className="border-t border-[#c6dff3] pt-4" data-testid="skipped-affidavit-divider">
                {renderDocumentRow('skip_affidavit', {
                  appearance: 'compact',
                  tone: 'skipped',
                  pmcSectionMilestone: 2,
                  labelOverride: 'Skip Affidavit (eSigned via portal) — same calendar month',
                })}
              </div>
              {renderFormSectionActions(2)}
            </section>
          ) : null}

          {reimbursementType === 'Eviction' ? (
            <section
              className="space-y-4 rounded-xl border border-[#5aa9ec] bg-[#eef6ff] p-4 text-[#173f7a] sm:p-6"
              data-testid="eviction-unique-requirements"
            >
              <h3 className="text-sm font-bold text-[#173f7a]">Eviction — Unique Requirements</h3>

              <div className="space-y-3 text-sm text-[#173f7a]">
                <div>
                  <p className="font-semibold text-[#173f7a]">Eviction Lawsuit Filed in Court</p>
                  <p>Same first calendar month rent was not paid</p>
                  <ul className="list-disc space-y-0.5 pl-6">
                    <li>Accepted: Unlawful Detainer, Application for Ejectment, Dispossessory Warrant</li>
                    <li>Alternates accepted at reduced reimb: Notice of Hearing, Judgement, Writ of Possession</li>
                    <li>NOT accepted: filing receipts, online summaries, dockets, 3rd-party filings</li>
                    <li>If applicable, 30-Day Notice to Vacate must be provided</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-[#173f7a]">Additional Circumstances (if applicable)</p>
                  <ul className="list-disc space-y-0.5 pl-6">
                    <li>Bankruptcy: Bankruptcy Document</li>
                    <li>Appeal: Appeal Document and Court Registry</li>
                    <li>Stipulation: Stipulation Agreement, Default Stipulation, and Writ of Ejectment</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-[#173f7a]">Partial Payments</p>
                  <ul className="list-disc space-y-0.5 pl-6">
                    <li>If eviction is restarted, or partial payment are received, reimbursements may be reduced</li>
                    <li>Liberty Rent will adjusts period to maximize reimbursement</li>
                  </ul>
                </div>
              </div>

              <ReasonForIncidentSelect
                register={register}
                options={incidentReasonOptions}
                {...reimbursementValueFieldProps(isConcluded, uniqueSectionDisabled)}
              />

              <div
                className="space-y-4 rounded-xl border border-[#bfd8ee] bg-[#f4f8ff] p-4"
                data-testid="eviction-legal-documents"
              >
                <h3 className="text-sm font-semibold text-[#173f7a]">Legal Documents</h3>

                <YesNoRadioGroup
                  name="cares_act_applicable_eviction"
                  label="Is a 30 day notice required at this property?"
                  value={extended.cares_act_applicable}
                  onChange={(val) => updateExtended({ cares_act_applicable: val })}
                  {...reimbursementValueFieldProps(isConcluded, uniqueSectionDisabled)}
                  tone="eviction"
                />
                {extended.cares_act_applicable === true ? (
                  <div className="border-l border-[#c6dff3] pl-7" data-testid="eviction-notice-nested">
                    {renderDocumentRow('cares_act_notice', {
                      appearance: 'compact',
                      tone: 'eviction',
                      pmcSectionMilestone: 2,
                      forceRender: true,
                    })}
                  </div>
                ) : null}

                <YesNoRadioGroup
                  name="skipped_during_eviction"
                  label="Did the resident vacate the unit before an eviction lawsuit was filed in the court?"
                  value={extended.skipped_during_eviction}
                  onChange={handleSkippedDuringEvictionChange}
                  {...reimbursementValueFieldProps(isConcluded, uniqueSectionDisabled)}
                  tone="eviction"
                />

                {extended.skipped_during_eviction === true ? (
                  <div className="space-y-3 border-l border-[#c6dff3] pl-7">
                    <p className="text-sm text-[#315b8a]">
                      Please complete the following Skip Affidavit to fulfill the legal requirement.
                    </p>
                    {renderDocumentRow('skip_affidavit', {
                      appearance: 'compact',
                      tone: 'eviction',
                      pmcSectionMilestone: 2,
                    })}
                  </div>
                ) : null}

                <div
                  className={cn(
                    'space-y-2',
                    evictionFullPathDisabled && !isConcluded && 'opacity-50',
                  )}
                >
                  {renderDocumentRow('eviction_lawsuit', {
                    appearance: 'compact',
                    disabled: !isConcluded && evictionFullPathDisabled,
                    pmcSectionMilestone: 2,
                    forceRender: true,
                    headerExtra: <EvictionLawsuitHelpModal disabled={evictionFullPathDisabled} />,
                    tone: 'eviction',
                  })}
                </div>

                <div
                  className={cn(
                    'space-y-2',
                    evictionFullPathDisabled && !isConcluded && 'opacity-50',
                  )}
                >
                  <YesNoRadioGroup
                    name="alternate_court_document_applicable"
                    label="Would you like to provide an Alternate Court-Issued Document?"
                    value={extended.alternate_court_document_applicable}
                    onChange={(val) => updateExtended({ alternate_court_document_applicable: val })}
                    {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                    tone="eviction"
                  />
                  <AlternateCourtDocHelpModal disabled={evictionFullPathDisabled} />
                  {extended.alternate_court_document_applicable === true ? (
                    <div className="border-l border-[#c6dff3] pl-7">
                      {renderDocumentRow('alternate_court_document', {
                        appearance: 'compact',
                        tone: 'eviction',
                        pmcSectionMilestone: 2,
                      })}
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                className={cn(
                  'space-y-4 rounded-xl border border-[#bfd8ee] bg-[#f4f8ff] p-4',
                  evictionFullPathDisabled && !isConcluded && 'opacity-50',
                )}
                data-testid="eviction-support-services"
              >
                <h3 className="text-sm font-semibold text-[#173f7a]">Eviction Support Services</h3>
                <YesNoRadioGroup
                  name="using_third_party_eviction_services"
                  label="Are you using third party eviction services?"
                  value={extended.using_third_party_eviction_services}
                  onChange={(val) => updateExtended({ using_third_party_eviction_services: val })}
                  {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                  tone="eviction"
                />
                {extended.using_third_party_eviction_services === true ? (
                  <label className="flex flex-col gap-1 text-sm font-medium text-[#173f7a]">
                    Servicer Name:
                    <input
                      className={cn(fieldClass, 'bg-white text-[#173f7a]')}
                      placeholder="Enter servicer name"
                      value={extended.third_party_servicer_name}
                      onChange={(e) => updateExtended({ third_party_servicer_name: e.target.value })}
                      readOnly={isConcluded && evictionFullPathDisabled}
                      disabled={!isConcluded && evictionFullPathDisabled}
                    />
                  </label>
                ) : null}
                <YesNoRadioGroup
                  name="utilizing_legal_team_for_eviction"
                  label="Are you utilizing legal team/counsel for eviction processing?"
                  value={extended.utilizing_legal_team_for_eviction}
                  onChange={(val) => updateExtended({ utilizing_legal_team_for_eviction: val })}
                  {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                  tone="eviction"
                />
              </div>

              <div
                className={cn(
                  'space-y-4 rounded-xl border border-[#bfd8ee] bg-[#f4f8ff] p-4',
                  evictionFullPathDisabled && !isConcluded && 'opacity-50',
                )}
                data-testid="eviction-additional-circumstances"
              >
                <h3 className="text-sm font-semibold text-[#173f7a]">Additional Circumstances</h3>
                <YesNoRadioGroup
                  name="bankruptcy_applicable"
                  label="Did the resident file for bankruptcy?"
                  value={extended.bankruptcy_applicable}
                  onChange={(val) => updateExtended({ bankruptcy_applicable: val })}
                  {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                  tone="eviction"
                />
                {extended.bankruptcy_applicable === true ? (
                  <div className="border-l border-[#c6dff3] pl-7">
                    {renderDocumentRow('bankruptcy_document', {
                      appearance: 'compact',
                      tone: 'eviction',
                      pmcSectionMilestone: 2,
                    })}
                  </div>
                ) : null}

                <YesNoRadioGroup
                  name="appeal_applicable"
                  label="Did the resident appeal the eviction?"
                  value={extended.appeal_applicable}
                  onChange={(val) => updateExtended({ appeal_applicable: val })}
                  {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                  tone="eviction"
                />
                {extended.appeal_applicable === true ? (
                  <div
                    className="space-y-4 border-l border-[#c6dff3] pl-7"
                    data-testid="eviction-appeal-nested"
                  >
                    {renderDocumentRow('court_registry', {
                      appearance: 'compact',
                      tone: 'eviction',
                      pmcSectionMilestone: 2,
                    })}
                    <YesNoRadioGroup
                      name="resident_paid_into_court"
                      label="Did the resident pay into the court?"
                      value={extended.resident_paid_into_court}
                      onChange={(val) => updateExtended({ resident_paid_into_court: val })}
                      {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                      tone="eviction"
                    />
                    {extended.resident_paid_into_court === true ? (
                      <div className="border-l border-[#c6dff3] pl-7">
                        {renderDocumentRow('bond_registry_receipt', {
                          appearance: 'compact',
                          tone: 'eviction',
                          pmcSectionMilestone: 2,
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="space-y-1">
                  <YesNoRadioGroup
                    name="stipulation_applicable"
                    label="Was a court-ordered Stipulation issued?"
                    value={extended.stipulation_applicable}
                    onChange={(val) => updateExtended({ stipulation_applicable: val })}
                    {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                    tone="eviction"
                  />
                  <p className="text-sm font-medium text-blue-600">
                    Only court-ordered payment plans are accepted.
                  </p>
                </div>
                {extended.stipulation_applicable === true ? (
                  <div
                    className="space-y-4 border-l border-[#c6dff3] pl-7"
                    data-testid="eviction-stipulation-nested"
                  >
                    {renderDocumentRow('stipulation_agreement', {
                      appearance: 'compact',
                      tone: 'eviction',
                      pmcSectionMilestone: 2,
                    })}
                    <YesNoRadioGroup
                      name="resident_defaulted_on_repayment"
                      label="Did the resident default on a Stipulation Agreement?"
                      value={extended.resident_defaulted_on_repayment}
                      onChange={(val) => updateExtended({ resident_defaulted_on_repayment: val })}
                      {...reimbursementValueFieldProps(isConcluded, evictionFullPathDisabled)}
                      tone="eviction"
                    />
                    {extended.resident_defaulted_on_repayment === true ? (
                      <div className="border-l border-[#c6dff3] pl-7">
                        {renderDocumentRow('default_stipulation', {
                          appearance: 'compact',
                          tone: 'eviction',
                          pmcSectionMilestone: 2,
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {renderFormSectionActions(2)}
            </section>
          ) : null}

          <section
            className="space-y-4 rounded-xl border border-slate-300 bg-white p-4 sm:p-6"
            data-testid="reimbursement-final-information"
          >
            <h3 className="text-sm font-bold text-slate-900">Final Information</h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={fieldLabelClass}>
                Move-out Date
                <ResidentDateField
                  control={control}
                  name="moveout_date"
                  {...reimbursementValueFieldProps(isConcluded, finalSectionDisabled)}
                  error={!!errors.moveout_date}
                />
              </label>
            </div>

            <div className="space-y-4">
              {extended.lease_modifications_at_move_in === true
                ? renderDocumentRow('original_ledger', {
                    appearance: 'compact',
                    helperText: 'Original ledger reflecting lease terms at move-in, required due to unit transfer',
                    pmcSectionMilestone: 3,
                  })
                : null}
              {renderDocumentRow('final_ledger', {
                appearance: 'compact',
                helperText: 'All transactions move-in through move-out; preliminary ledger OK during review',
                pmcSectionMilestone: 3,
              })}
            </div>

            <div className="space-y-4 border-t border-slate-200 pt-4" data-testid="new-resident-information">
              <h4 className="text-sm font-semibold text-slate-800">New Resident Information</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className={fieldLabelClass}>
                  New Resident Lease Start Date
                  <ResidentDateField
                    control={control}
                    name="new_resident_lease_start_date"
                    {...reimbursementValueFieldProps(isConcluded, finalSectionDisabled)}
                    error={!!errors.new_resident_lease_start_date}
                  />
                </label>
                <label className={fieldLabelClass}>
                  New Resident Move-in Date
                  <ResidentDateField
                    control={control}
                    name="movein_date"
                    {...reimbursementValueFieldProps(isConcluded, finalSectionDisabled)}
                    error={!!errors.movein_date}
                  />
                </label>
              </div>
            </div>

            <div className="space-y-3 border-t border-slate-200 pt-4" data-testid="payment-information">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-slate-800">Payment Information</h4>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-sm font-medium',
                    paymentInfoVerified ? 'text-emerald-700' : 'text-amber-700',
                  )}
                  data-testid="payment-verification-status"
                >
                  {paymentInfoVerified ? <Check size={14} aria-hidden="true" /> : null}
                  {paymentInfoVerified ? 'Verified' : 'Unverified'}
                </span>
              </div>
              <div className="space-y-1 text-sm text-slate-700">
                <p className="font-semibold text-slate-900">{paymentMethodLabel}</p>
                <p>
                  <span className="font-medium">Payee:</span> {paymentPayeeLabel}
                </p>
                <p>
                  <span className="font-medium">Address:</span> {paymentAddressLabel}
                </p>
              </div>
              <p className="flex items-start gap-2 text-xs text-blue-700">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  To change or update your preferred payment method, please email{' '}
                <a href="mailto:propertysupport@libertyrent.com" className="text-blue-600 hover:underline">
                  propertysupport@libertyrent.com
                </a>
                </span>
              </p>
            </div>
            {renderFormSectionActions(3)}
          </section>

          <div className={cn(entityActionButtonClusterClass, 'border-t border-slate-200 pt-4')}>
            {isLibertyRole ? (
              <>
                <button
                  type="button"
                  disabled={
                    saveDisabled ||
                    isConcluded ||
                    !canSubmitForReview ||
                    submitForReviewLoading ||
                    !onSubmitForLibertyReview
                  }
                  onClick={() => {
                    if (!onSubmitForLibertyReview || submitForReviewLoading) return
                    void (async () => {
                      setSubmitForReviewLoading(true)
                      try {
                        await onSubmitForLibertyReview()
                      } finally {
                        setSubmitForReviewLoading(false)
                      }
                    })()
                  }}
                  className={cn(primaryEntityActionButtonClass, 'inline-flex items-center gap-2')}
                  data-testid="reimbursement-request-submit-for-review"
                  aria-busy={submitForReviewLoading}
                >
                  {submitForReviewLoading ? (
                    <ActionButtonSpinner size={16} className="text-white" />
                  ) : (
                    <Send size={16} aria-hidden="true" />
                  )}
                  {submitForReviewLoading ? 'Submitting…' : 'Submit for Review'}
                </button>
              </>
            ) : null}
            {onCancelReimbursement ? (
              <button
                type="button"
                disabled={isConcluded}
                onClick={() => {
                  if (isConcluded) return
                  setShowCancelModal(true)
                }}
                className={deleteEntityActionButtonClass}
                data-testid="reimbursement-request-cancel"
              >
                <Trash2 size={16} aria-hidden="true" />
                Cancel Reimbursement Request
              </button>
            ) : null}
          </div>
        </div>
      </ReimbursementFormStickyLayout>

      {isBelowLg ? (
        <ReimbursementSectionSubmitConfirm
          pendingMilestoneId={pendingMobileSubmitMilestoneId}
          onConfirm={handleSectionSubmit}
          onDismiss={() => setPendingMobileSubmitMilestoneId(null)}
        />
      ) : null}

      <EvictionLawsuitWarningModal
        isOpen={showEvictionWarning}
        onCancel={() => {
          setShowEvictionWarning(false)
          setPendingEvictionLawsuitFile(null)
        }}
        onContinue={() => {
          setShowEvictionWarning(false)
          const file = pendingEvictionLawsuitFile
          setPendingEvictionLawsuitFile(null)
          if (!file || !onImmediateDocumentUpload) return
          const uploadField = getApiUploadFieldForDocType('eviction_lawsuit')?.fieldName
          if (uploadField) {
            void onImmediateDocumentUpload(uploadField, [file])
          }
        }}
      />

      <CancelReimbursementConfirmModal
        isOpen={showCancelModal}
        confirmValue={cancelConfirmValue}
        onConfirmValueChange={setCancelConfirmValue}
        onClose={() => {
          setShowCancelModal(false)
          setCancelConfirmValue('')
        }}
        onConfirm={() => {
          setShowCancelModal(false)
          setCancelConfirmValue('')
          onCancelReimbursement?.()
        }}
      />

    </div>
  )
}

function lookupIncompleteReasonDescription(
  label: string,
  reasons: DocumentReviewIncompleteReason[],
): string {
  return reasons.find((entry) => entry.label === label)?.description ?? ''
}

function toMonthInputValue(month: unknown, year: unknown): string {
  const yearText = String(year ?? '').trim()
  const monthNumber = Number(month)
  if (!yearText || !Number.isFinite(monthNumber) || monthNumber < 1 || monthNumber > 12) return ''
  return `${yearText}-${String(monthNumber).padStart(2, '0')}`
}

function getDownloadKey(docType: ReimbursementDocumentType): string {
  switch (docType) {
    case 'signed_lease':
      return 'LEASE'
    case 'current_ledger':
    case 'final_ledger':
      return 'PAYMENT_LEDGER'
    case 'skip_affidavit':
      return 'SKIP_CLAIM'
    case 'eviction_lawsuit':
      return 'UDFILE'
    default:
      return 'DOCUMENT'
  }
}
